import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../LanguageSelector';
import { listProjects } from '../../portfolio/registry';

export function Header() {
  const { t } = useTranslation('common');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navigation: any[] = [
    { name: t('nav.home'), href: '/' },
    {
      name: t('nav.portfolio'),
      dropdown: true,
      items: listProjects().map((p) => ({ name: t(p.navKey), href: `/portfolio/${p.slug}` })),
    },
    { name: t('nav.contact'), href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-4 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? 'bg-dark-100/80 shadow-xl shadow-black/20'
        : 'bg-dark/60'
        } backdrop-blur-xl rounded-2xl mx-4 border border-white/5`}
    >
      <nav className="max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex-shrink-0 font-bold text-xl tracking-tight transition-all duration-300 cursor-pointer focus-ring rounded-md px-2 py-1"
          >
            <span className="text-neon-cyan">Happy</span>
            <span className="text-white">Tech</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:gap-8">
            {navigation.map((item) => {
              if (item.dropdown) {
                const isActive = item.items?.some((subItem: any) => location.pathname === subItem.href);
                return (
                  <div key={item.name} className="relative group">
                    <button className={`flex items-center gap-1 text-sm font-medium transition-all duration-300 relative cursor-pointer focus-ring px-2 py-1 rounded-md ${isActive ? 'text-neon-cyan' : 'text-text-secondary hover:text-neon-cyan group-hover:text-neon-cyan'}`}>
                      {item.name}
                      <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
                      {isActive && (
                        <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-neon-cyan to-neon-purple rounded-full transition-all duration-300 shadow-glow-cyan" />
                      )}
                    </button>
                    {/* Dropdown Menu */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-56 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-300 z-50">
                      <div className="bg-dark-100 border border-white/10 rounded-xl p-2 shadow-2xl shadow-black/60">
                        {item.items?.map((subItem: any) => {
                          const isSubActive = location.pathname === subItem.href;
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`block px-4 py-2.5 text-sm rounded-lg transition-colors ${isSubActive ? 'bg-white/10 text-neon-cyan' : 'text-text-secondary hover:text-neon-cyan hover:bg-white/10'
                                }`}
                            >
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium transition-all duration-300 relative cursor-pointer focus-ring px-2 py-1 rounded-md ${isActive
                    ? 'text-neon-cyan'
                    : 'text-text-secondary hover:text-neon-cyan'
                    }`}
                >
                  {item.name}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-neon-cyan to-neon-purple rounded-full transition-all duration-300 shadow-glow-cyan" />
                  )}
                </Link>
              );
            })}
            <LanguageSelector />
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden p-2 text-text-secondary hover:text-neon-cyan hover:bg-white/5 rounded-lg transition-all duration-300 focus-ring"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10 mt-4 rounded-b-xl">
            <div className="space-y-2">
              {navigation.map((item) => {
                if (item.dropdown) {
                  return (
                    <div key={item.name} className="space-y-1 mb-2">
                      <div className="px-4 py-2 text-sm font-medium text-text-secondary/50 uppercase tracking-widest">{item.name}</div>
                      {item.items?.map((subItem: any) => {
                        const isSubActive = location.pathname === subItem.href;
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`block px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer focus-ring ${isSubActive
                              ? 'bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/20'
                              : 'text-text-secondary hover:bg-white/5 hover:text-neon-cyan'
                              }`}
                          >
                            {subItem.name}
                          </Link>
                        );
                      })}
                    </div>
                  );
                }

                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer focus-ring ${isActive
                      ? 'bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/20'
                      : 'text-text-secondary hover:bg-white/5 hover:text-neon-cyan'
                      }`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
            {/* Add language selector to mobile menu */}
            <div className="px-4 pt-2">
              <LanguageSelector />
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
